import { createSelector } from '@ngrx/store';
import { DateTime } from 'luxon';

import { User } from '@apps/auth/domain/models/back';
import { Comparer } from '@common/services';

import { BackConstants } from '@apps/back/back.constants';
import { filterQuickSearch } from '@common/services/utils.service';

import { getBackAuthUsersState } from '../users.selectors';
import { IBackAuthUsersState } from '../users.state';
import { usersAdapter } from '../users.common';
import { BackAuthUsersInternalFeatureKey, IBackAuthUsersInternalState } from './users-internal.reducer';

export const getBackAuthUsersInternalState = createSelector(
    getBackAuthUsersState,
    (state: IBackAuthUsersState) =>
        state[BackAuthUsersInternalFeatureKey]
);

export const getUsersInternalCounters = createSelector(
    getBackAuthUsersInternalState,
    (state: IBackAuthUsersInternalState) =>
        state.counters
);

export const getUsersInternalQueryString = createSelector(
    getBackAuthUsersInternalState,
    (state: IBackAuthUsersInternalState) =>
        state.oData.qs
);
export const getBackAuthInternalUsersState = createSelector(
    getBackAuthUsersInternalState,
    (state: IBackAuthUsersInternalState) =>
        state.users
);
export const {
    selectAll: getAllUsersInternal,
    selectTotal: getTotalInternalUsers,
} = usersAdapter.getSelectors(getBackAuthInternalUsersState);
export const getUsersInternalActive = createSelector(
    getAllUsersInternal,
    (users: User[]): User[] =>
        users.filter((user: User) => user.active)
);
const getQuickSearchedUsersInternal = createSelector(
    getBackAuthUsersInternalState,
    getAllUsersInternal,
    (state: IBackAuthUsersInternalState, users: User[]) =>
        filterQuickSearch(users, state.qsWord, BackConstants.usersQSFields)
);
export const getPipelinedUsersInternal = createSelector(
    getQuickSearchedUsersInternal,
    (users: User[], params: { comparer: Comparer<User> }) =>
        [...users].sort(params.comparer)
);
const getQuickSearchedUsersInternalImpe = createSelector(
    getBackAuthUsersInternalState,
    getUsersInternalActive,
    (state: IBackAuthUsersInternalState, users: User[]) =>
        filterQuickSearch(users, state.qsWordImpe, BackConstants.usersQSFields)
);
export const getUsersInternalImpePipelined = createSelector(
    getQuickSearchedUsersInternalImpe,
    (users: User[]) => users
    // (users: User[], params: { comparer: Comparer<User> }) =>
    //     [...users].sort(params.comparer)
);
export const getEntityInternalUsers = createSelector(
    getUsersInternalImpePipelined,
    (users: User[], { id }: { id: string }) =>
        users.filter((u: User) => u.entityId === id)
);
export const getUsersInternalLoadingStatus = createSelector(
    getBackAuthUsersInternalState,
    (state) => state.isLoading
);
export const getUsersInternalLoadedStatus = createSelector(
    getBackAuthUsersInternalState,
    (state) => state.isLoaded
);
export const getUsersInternalOupsStatus = createSelector(
    getBackAuthUsersInternalState,
    (state) => state.isOups
);
export const getUsersInternalEmptyStatus = createSelector(
    getBackAuthUsersInternalState,
    (state) => state.isEmpty
);
export const getUsersInternalIsDataValid = createSelector(
    getBackAuthUsersInternalState,
    (state) =>
        state.expirationDate !== null && DateTime.utc() < state.expirationDate
);

export const getUsersInternalProfile = createSelector(
    getAllUsersInternal,
    (users: User[], params: { userName: string }): User =>
    {
        const normalizedUserName = params.userName.toUpperCase();

        return users.find((user: User) =>
            user.normalizedUserName === normalizedUserName
        );
    }
);
